import { useCallback, useEffect, useRef, useState } from 'react';
import { listChatSessions } from '../../api/chatApi';
import { getCreditsBalance } from '../../api/creditApi';
import { getSubscriptionStatus } from '../../api/subscriptionApi';
import { subscribeToCreditState } from '../../helper/creditStateEvents';
import { ensureOkResponse } from './chatMessageModel';

export default function useChatNavigationData({ sessionLimit = 5, sessionsEnabled = true } = {}) {
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState(null);
  const [creditSummary, setCreditSummary] = useState(null);
  const [creditLoading, setCreditLoading] = useState(true);
  const [creditError, setCreditError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const sessionSequence = useRef(0);
  const creditSequence = useRef(0);
  const subscriptionSequence = useRef(0);

  const refreshSessions = useCallback(async () => {
    const sequence = ++sessionSequence.current;
    if (!sessionsEnabled) {
      setSessions([]);
      setSessionsError(null);
      setSessionsLoading(false);
      return;
    }
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const response = await listChatSessions(sessionLimit);
      const data = ensureOkResponse(response, 'Unable to load chats.');
      if (sequence !== sessionSequence.current) return;
      setSessions(Array.isArray(data.sessions) ? data.sessions : []);
    } catch (error) {
      if (sequence !== sessionSequence.current) return;
      setSessionsError(error.message || 'Unable to load chats.');
    } finally {
      if (sequence === sessionSequence.current) setSessionsLoading(false);
    }
  }, [sessionLimit, sessionsEnabled]);

  const refreshCredits = useCallback(async () => {
    const sequence = ++creditSequence.current;
    setCreditLoading(true);
    setCreditError(null);
    try {
      const response = await getCreditsBalance();
      const data = ensureOkResponse(response, 'Credits unavailable');
      if (sequence !== creditSequence.current) return;
      setCreditSummary(data);
    } catch (error) {
      if (sequence !== creditSequence.current) return;
      setCreditError(error.message || 'Credits unavailable');
      setCreditSummary(null);
    } finally {
      if (sequence === creditSequence.current) setCreditLoading(false);
    }
  }, []);

  const refreshSubscription = useCallback(async () => {
    const sequence = ++subscriptionSequence.current;
    setSubscriptionLoading(true);
    setSubscriptionError(null);
    try {
      const response = await getSubscriptionStatus();
      const data = ensureOkResponse(response, 'Unable to load subscription status.');
      if (sequence !== subscriptionSequence.current) return;
      setSubscriptionStatus(data);
    } catch (error) {
      if (sequence !== subscriptionSequence.current) return;
      setSubscriptionError(error.message || 'Unable to load subscription status.');
      setSubscriptionStatus(null);
    } finally {
      if (sequence === subscriptionSequence.current) setSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSessions();
    refreshCredits();
    refreshSubscription();
  }, [refreshCredits, refreshSessions, refreshSubscription]);

  // A chat turn that settles a charge or refund publishes on the credit-state bus;
  // without this the sidebar balance only caught up on the next turn.
  useEffect(() => subscribeToCreditState((event) => {
    if (event.clearRefusal) void refreshCredits();
  }), [refreshCredits]);

  return {
    sessions,
    sessionsLoading,
    sessionsError,
    refreshSessions,
    creditSummary,
    creditLoading,
    creditError,
    refreshCredits,
    subscriptionStatus,
    subscriptionLoading,
    subscriptionError,
    refreshSubscription,
  };
}
